import { PLAN_ID, type SEOReportPlanId } from "@/lib/seo-intents";
import { safeSessionSet } from "@/lib/browser-storage";

export interface ReportFollowupContext {
  planId: SEOReportPlanId;
  orderId: string;
  headline: string;
  body: string;
  prompt: string;
}

/**
 * Single source of truth for per-plan follow-up copy AND the next upsell plan.
 * agent-decision.ts reads `nextPlanId` to pick the follow-up offer.
 * buildReportFollowupContext reads the copy fields.
 */
export const REPORT_FOLLOWUP_MAP: Record<
  SEOReportPlanId,
  { nextPlanId: SEOReportPlanId; headline: string; body: string; prompt: string }
> = {
  [PLAN_ID.brihat_kundali]: {
    nextPlanId: PLAN_ID.annual_forecast,
    headline: "Your kundali report is ready. Now ask what it means.",
    body: "Start with the one house, dasha, or dosha that is repeating in your life. The agent can turn the report into a 40-day upay routine.",
    prompt: "Explain the top 3 patterns in my kundali report and build my 40-day upay routine.",
  },
  [PLAN_ID.marriage_report]: {
    nextPlanId: PLAN_ID.dosha_remedy_plan,
    headline: "Your marriage report is ready. Now check the repeating pattern.",
    body: "Ask the agent to explain the 7th house, Shukra, Mangal Dosh, and timing before choosing any upay.",
    prompt: "Explain my marriage report and tell me which upay fits my chart before I act.",
  },
  [PLAN_ID.career_report]: {
    nextPlanId: PLAN_ID.annual_forecast,
    headline: "Your career report is ready. Now find the blockage.",
    body: "Ask the agent to explain the 10th house, Shani pattern, and dasha window behind the career delay.",
    prompt: "Explain my career report and build a practical upay plan for my current dasha.",
  },
  [PLAN_ID.annual_forecast]: {
    nextPlanId: PLAN_ID.muhurat_report,
    headline: "Your annual bhavishya is ready. Now choose the right months.",
    body: "Ask the agent to turn the forecast into action windows, caution periods, and simple upay.",
    prompt: "Explain my annual forecast and list the months to act, wait, and do upay.",
  },
  [PLAN_ID.sade_sati]: {
    nextPlanId: PLAN_ID.dosha_remedy_plan,
    headline: "Your Sade Sati report is ready. Now follow Shani with clarity.",
    body: "Ask the agent to explain your phase, what Shani is testing, and which upay should stay light and consistent.",
    prompt: "Explain my Sade Sati report and build my safe Shani upay routine.",
  },
  [PLAN_ID.pitra_dosh]: {
    nextPlanId: PLAN_ID.dosha_remedy_plan,
    headline: "Your Pitra Dosh report is ready. Now check the right upay.",
    body: "Ask the agent whether the dosh is strong, whether cancellation applies, and what ancestral upay to do first.",
    prompt: "Explain my Pitra Dosh report and list the upay sequence I should follow.",
  },
  [PLAN_ID.mahadasha_forecast]: {
    nextPlanId: PLAN_ID.annual_forecast,
    headline: "Your Mahadasha forecast is ready. Now turn timing into action.",
    body: "Ask the agent to connect the current dasha with the next 12 months, decision windows, and upay.",
    prompt: "Explain my Mahadasha forecast and tell me which months to act or wait.",
  },
  [PLAN_ID.muhurat_report]: {
    nextPlanId: PLAN_ID.annual_forecast,
    headline: "Your muhurat plan is ready. Now confirm the sankalp.",
    body: "Ask the agent why this timing fits your city, Moon sign, dasha, and exact purpose.",
    prompt: "Explain my muhurat report and write the sankalp I should take before starting.",
  },
  [PLAN_ID.dosha_remedy_plan]: {
    nextPlanId: PLAN_ID.brihat_kundali,
    headline: "Your dosha remedy plan is ready. Now follow the right upay.",
    body: "Ask the agent whether the dosha is strong, whether cancellation applies, and when mantra, product upay, or shanti is actually needed.",
    prompt: "Explain my dosha remedy plan and build my safe 40-day upay routine.",
  },
};

export function buildReportFollowupContext(
  planId: SEOReportPlanId,
  orderId: string
): ReportFollowupContext {
  const { headline, body, prompt } = REPORT_FOLLOWUP_MAP[planId];
  return { planId, orderId, headline, body, prompt };
}

export function saveReportFollowupContext(context: ReportFollowupContext) {
  if (typeof window === "undefined") return;
  try {
    safeSessionSet("ishvaram_post_purchase_followup", JSON.stringify(context));
  } catch {
    // The visible success panel still works when session storage is unavailable.
  }
}
